import { useEffect, useRef, useState } from 'react';

import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { ErrorState } from '@/shared/ui';

import {
  useDownloadTemplate,
  useGoogleSheetSettings,
  useImportFile,
  useImportFromGoogleSheet,
  useUpdateGoogleSheetSettings,
} from '../../hooks';
import { useCatalogPermissions } from '../../lib/useCatalogPermissions';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useToast } from '../../lib/useToast';
import type { ImportRun } from '../../model/types';

function runColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  if (status === 'SUCCESS') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'RUNNING') return 'warning';
  return 'default';
}

function RunSummary({ run }: { run: ImportRun }) {
  return (
    <Alert severity={run.status === 'FAILED' ? 'error' : 'success'} icon={false} sx={{ mt: 1 }}>
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip size="small" color={runColor(run.status)} label={run.status} />
          <Chip size="small" variant="outlined" label={run.source} />
          {run.dryRun ? <Chip size="small" variant="outlined" color="info" label="Проверка (dry-run)" /> : null}
        </Stack>
        <Typography variant="body2">
          Категорий: <b>{formatNumber(run.categoriesCount)}</b> · Товаров:{' '}
          <b>{formatNumber(run.productsCount)}</b>
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Начало: {formatDateTime(run.startedAt)}
          {run.finishedAt ? ` · Завершение: ${formatDateTime(run.finishedAt)}` : ''}
        </Typography>
        {run.error ? (
          <Typography variant="body2" color="error">
            {run.error}
          </Typography>
        ) : null}
      </Stack>
    </Alert>
  );
}

function resolveSpreadsheetUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed === '') return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://docs.google.com/spreadsheets/d/${encodeURIComponent(trimmed)}`;
}

/**
 * Bulk catalog import form — Excel template, file upload, and Google-Sheet sync.
 *
 * Self-contained (state, permission gate, hooks and feedback) so it can render
 * both as a standalone page (`ImportPage`) and inside a slideout (`ImportFlyout`)
 * on the Products workspace. It owns no layout chrome (no page/drawer wrapper).
 */
export function ImportPanel() {
  const { canView, canManage } = useCatalogPermissions();
  const { toast, success, error: notifyError, clear } = useToast();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(true);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [lastRun, setLastRun] = useState<ImportRun | null>(null);

  const download = useDownloadTemplate();
  const importFile = useImportFile();
  const importSheet = useImportFromGoogleSheet();
  const sheetSettings = useGoogleSheetSettings({ enabled: canView });
  const saveSheet = useUpdateGoogleSheetSettings();

  useEffect(() => {
    if (sheetSettings.data?.spreadsheetId) setSpreadsheetId(sheetSettings.data.spreadsheetId);
  }, [sheetSettings.data]);

  if (!canView) {
    return <ErrorState title="Доступ ограничен" message="У вас нет прав на просмотр каталога." />;
  }

  const runFileImport = () => {
    if (!file) return;
    importFile.mutate(
      { file, dryRun },
      {
        onSuccess: (run) => {
          setLastRun(run);
          success(dryRun ? 'Файл проверен.' : 'Импорт выполнен.');
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  const runSheetImport = () => {
    importSheet.mutate(
      { dryRun },
      {
        onSuccess: (run) => {
          setLastRun(run);
          success(dryRun ? 'Таблица проверена.' : 'Импорт выполнен.');
        },
        onError: (error) => notifyError(error.message),
      },
    );
  };

  return (
    <>
      <Stack spacing={2.5}>
        <FormControlLabel
          control={<Switch checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} />}
          label="Режим проверки (dry-run) — только валидация, без записи"
        />

        {/* Excel file import */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Файл Excel
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Скачайте шаблон, заполните его в Excel и загрузите готовый файл обратно. Поддерживаются `.xlsx` и `.xls`.
            </Typography>
            <Stack spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={download.isPending ? <CircularProgress size={16} /> : <DownloadRoundedIcon />}
                disabled={download.isPending}
                onClick={() =>
                  download.mutate(undefined, {
                    onError: (error) => notifyError(error.message),
                  })
                }
                >
                  Скачать шаблон
                </Button>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<TableChartRoundedIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canManage}
                >
                  Выбрать файл
                </Button>
                <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1 }}>
                  {file?.name ?? 'Файл не выбран'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={importFile.isPending ? <CircularProgress size={16} color="inherit" /> : <CloudUploadRoundedIcon />}
                  disabled={!canManage || !file || importFile.isPending}
                  onClick={runFileImport}
                >
                  {dryRun ? 'Проверить' : 'Импортировать'}
                </Button>
              </Stack>
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </CardContent>
        </Card>

        {/* Google Sheet */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Google-таблица
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Сохраните ссылку на таблицу один раз — затем запускайте импорт повторно.
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <TextField
                  label="Ссылка или ID Google-таблицы"
                  value={spreadsheetId}
                  onChange={(e) => setSpreadsheetId(e.target.value)}
                  disabled={!canManage}
                  size="small"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  startIcon={saveSheet.isPending ? <CircularProgress size={16} /> : <SaveRoundedIcon />}
                  disabled={!canManage || spreadsheetId.trim() === '' || saveSheet.isPending}
                  onClick={() =>
                    saveSheet.mutate(
                      { spreadsheetId: spreadsheetId.trim() },
                      {
                        onSuccess: () => success('Настройка сохранена.'),
                        onError: (error) => notifyError(error.message),
                      },
                    )
                  }
                  >
                  Сохранить
                </Button>
              </Stack>
              <Divider />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 1.5,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  disabled={resolveSpreadsheetUrl(spreadsheetId) === ''}
                  component="a"
                  href={resolveSpreadsheetUrl(spreadsheetId) || undefined}
                  target="_blank"
                  rel="noreferrer"
                >
                  Открыть
                </Button>
                <Button
                  variant="contained"
                  startIcon={importSheet.isPending ? <CircularProgress size={16} color="inherit" /> : <PlayArrowRoundedIcon />}
                  disabled={!canManage || importSheet.isPending}
                  onClick={runSheetImport}
                  sx={{ minWidth: 232, whiteSpace: 'nowrap' }}
                >
                  {dryRun ? 'Проверить из таблицы' : 'Импортировать из таблицы'}
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Last run */}
        {lastRun ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700}>
                Результат последнего запуска
              </Typography>
              <RunSummary run={lastRun} />
            </CardContent>
          </Card>
        ) : null}
      </Stack>

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={clear}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={clear} severity={toast?.severity ?? 'info'} variant="filled" sx={{ width: '100%' }}>
          {toast?.message ?? ''}
        </Alert>
      </Snackbar>
    </>
  );
}
